import { useRef, useState, type PointerEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { TEXT_LANE_HEIGHT, type TextTrackItem, type TextPatch } from './textTrack'

interface Props {
  lanes: TextTrackItem[][]
  zoom: number
  revision: number
  selectedKey: string | null
  disabled: boolean
  onSelect: (key: string) => void
  onGestureStart: () => void
  onGestureEnd: (key: string, patch: TextPatch | null, revision: number) => void
  onChange: (key: string, patch: TextPatch, revision: number) => void
}
export function TextTrackLanes({ lanes, zoom, revision, selectedKey, disabled, onSelect, onChange, onGestureStart, onGestureEnd }: Props) {
  const { t } = useTranslation('canvas')
  const drag = useRef<{ key: string; revision: number; x: number; start: number; duration: number; mode: 'move' | 'start' | 'end'; patch: TextPatch } | null>(null)
  const [preview, setPreview] = useState<{ key: string; start_seconds: number; duration_seconds: number } | null>(null)
  function begin(event: PointerEvent<HTMLElement>, item: TextTrackItem, mode: 'move' | 'start' | 'end') {
    event.stopPropagation()
    if (disabled || event.button !== 0) { onSelect(item.key); return }
    onGestureStart()
    // Inspector fields commit on blur. Pointer capture must not unmount a draft first.
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur()
    onSelect(item.key)
    event.preventDefault()
    event.currentTarget.setPointerCapture(event.pointerId)
    drag.current = { key: item.key, revision, x: event.clientX, start: item.start_seconds, duration: item.duration_seconds, mode, patch: {} }
  }
  function move(event: PointerEvent<HTMLElement>) {
    const state = drag.current
    if (!state || disabled) return
    const delta = Math.round((event.clientX - state.x) / zoom * 100) / 100
    let start = state.start
    let duration = state.duration
    if (state.mode === 'move') start = Math.max(0, state.start + delta)
    else if (state.mode === 'start') { start = Math.max(0, Math.min(state.start + state.duration - 0.1, state.start + delta)); duration = state.start + state.duration - start }
    else duration = Math.max(0.1, state.duration + delta)
    state.patch = { start_seconds: start, ...(state.mode === 'move' ? {} : { duration_seconds: duration }) }
    setPreview({ key: state.key, start_seconds: start, duration_seconds: duration })
  }
  function finish(event: PointerEvent<HTMLElement>, cancel = false) {
    event.stopPropagation()
    const state = drag.current
    drag.current = null
    setPreview(null)
    if (state) onGestureEnd(state.key, !cancel && !disabled && Object.keys(state.patch).length ? state.patch : null, state.revision)
  }
  return <div data-testid="text-track-lanes">
    {lanes.map((lane, index) => <div key={index} className="relative border-b border-border-subtle bg-surface-0" style={{ height: TEXT_LANE_HEIGHT }}>
      {lane.map((item) => {
        const current = preview?.key === item.key && drag.current?.revision === revision ? preview : item
        return <div key={item.key} role="button" tabIndex={0} aria-label={item.text} aria-pressed={selectedKey === item.key}
          data-testid="text-track-clip" data-text-key={item.key}
          className={`absolute top-2 flex h-7 items-center overflow-hidden rounded border px-3 text-xs font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${selectedKey === item.key ? 'border-primary bg-primary/30 text-foreground' : 'border-input-border bg-agent-object/45 text-foreground hover:bg-agent-object/60 hover:border-input-border-hover'} ${item.visible === false ? 'opacity-40' : ''} ${disabled ? '' : 'cursor-grab'}`}
          style={{ left: current.start_seconds * zoom, width: Math.max(4, current.duration_seconds * zoom) }}
          onClick={(event) => event.stopPropagation()} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onSelect(item.key) } }}
          onPointerDown={(event) => begin(event, item, 'move')} onPointerMove={move} onPointerUp={finish} onPointerCancel={(event) => finish(event, true)} onLostPointerCapture={(event) => finish(event, true)}>
          <span className="truncate">{item.text}</span>
          {(['start', 'end'] satisfies ('start' | 'end')[]).map((edge) => <span key={edge} role="slider" tabIndex={disabled ? -1 : 0} aria-label={t(edge === 'start' ? 'textTrack.trimStart' : 'textTrack.trimEnd')}
            aria-valuenow={edge === 'start' ? current.start_seconds : current.start_seconds + current.duration_seconds}
            className={`absolute inset-y-0 w-2 cursor-ew-resize bg-foreground/20 hover:bg-foreground/50 ${edge === 'start' ? 'left-0' : 'right-0'}`}
            onKeyDown={(event) => { if (disabled || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return; event.preventDefault(); event.stopPropagation(); const delta = event.key === 'ArrowRight' ? 0.1 : -0.1; if (edge === 'end') onChange(item.key, { duration_seconds: Math.max(0.1, item.duration_seconds + delta) }, revision); else { const start = Math.max(0, Math.min(item.start_seconds + item.duration_seconds - 0.1, item.start_seconds + delta)); onChange(item.key, { start_seconds: start, duration_seconds: item.start_seconds + item.duration_seconds - start }, revision) } }}
            onPointerDown={(event) => begin(event, item, edge)} />)}
        </div>
      })}
    </div>)}
  </div>
}
